(() => {
  'use strict';
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const money = (cents, currency) => { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100); } catch (_error) { return `$${(Number(cents || 0) / 100).toFixed(2)}`; } };
  const previewParameters = ['preview_theme_id', '_avid_publication', '_avid_release', '_fd', 'pb', 'currency'];
  const previewUrl = (url) => {
    const target = new URL(url, window.location.origin);
    const current = new URL(window.location.href || window.location.origin);
    previewParameters.forEach((name) => {
      if (!target.searchParams.has(name) && current.searchParams.has(name)) target.searchParams.set(name, current.searchParams.get(name));
    });
    return target;
  };
  const sectionUrl = (url, sectionId = 'component-center-results') => {
    const target = previewUrl(url);
    target.searchParams.set('section_id', sectionId);
    return target;
  };
  const legacyCategoryAliases = {
    kit_maverick_r: 'maverick_r',
    kit_maverick_x3: 'maverick_x3',
    kit_defender: 'defender',
    kit_rzr_pro_r: 'rzr_pro',
    kit_rzr_pro_s: 'rzr_pro',
    kit_yxz: 'yxz1000r',
    kit_universal: 'universal',
    specialty_interfaces: 'measured_interfaces',
    specialty_fabrication: 'all',
    specialty_other: 'all'
  };
  const requestedCategory = (url, fallback = 'all') => {
    const target = new URL(url, window.location.origin);
    const canonical = normalize(target.searchParams.get('component_category'));
    if (canonical) return canonical === 'bearings_pivots' ? 'bearings_linkage' : canonical;
    const legacy = normalize(target.searchParams.get('ti_kit') || target.searchParams.get('ti_specialty'));
    return legacyCategoryAliases[legacy] || normalize(fallback) || 'all';
  };
  const requestedResultsUrl = (pageUrl, baseUrl, familyCollections = []) => {
    const page = new URL(pageUrl, window.location.origin);
    const stored = page.searchParams.get('component_filters');
    const base = previewUrl(baseUrl);
    if (stored) {
      try {
        const saved = previewUrl(stored);
        const allowedPaths = [base.pathname, ...familyCollections.map((url) => new URL(url, base).pathname)];
        if (saved.origin === base.origin && allowedPaths.includes(saved.pathname)) return saved;
      } catch (_error) { /* Invalid stale URL falls back to this category. */ }
    }
    if (page.pathname === base.pathname) {
      page.searchParams.forEach((value, name) => {
        if (name.startsWith('filter.') || name === 'sort_by' || name === 'page') base.searchParams.append(name, value);
      });
    }
    return base;
  };
  // Native collection filters and pagination already rendered by Shopify can be enhanced in place.
  const canReuseServerResults = (pageUrl, resultsUrl) => {
    const page = new URL(pageUrl, window.location.origin);
    const result = new URL(resultsUrl, window.location.origin);
    const filterKey = (url) => [...url.searchParams.entries()]
      .filter(([key]) => key.startsWith('filter.') || ['page', 'sort_by'].includes(key))
      .map(([key, value]) => `${key}=${value}`).sort().join('&');
    return page.origin === result.origin && page.pathname === result.pathname && filterKey(page) === filterKey(result);
  };
  const requestedFamilies = (url) => new URL(url, window.location.origin).searchParams
    .getAll('filter.p.product_type')
    .flatMap((value) => value.split(','))
    .map(normalize)
    .filter(Boolean)
    .sort();
  const familyValues = (link) => normalize(link.dataset.componentFamily).split('|').filter(Boolean).sort();
  const familySelected = (link, url, baseUrl) => {
    const result = new URL(url, window.location.origin);
    const collection = new URL(link.dataset.componentFamilyCollection || baseUrl, window.location.origin);
    return result.pathname === collection.pathname && (link.dataset.componentFamilyCollection
      ? true : sameFamilies(familyValues(link), requestedFamilies(url)));
  };
  const sameFamilies = (left, right) => left.length === right.length && left.every((value, index) => value === right[index]);
  const testHook = globalThis.__AVID_COMPONENT_CENTER_TEST_HOOK__;

  const createFamilySelect = (navigation, links) => {
    if (!navigation || !links.length) return null;
    const label = document.createElement('label');
    label.className = 'component-center__category-select';
    const heading = document.createElement('span');
    heading.textContent = 'Shop by category';
    const select = document.createElement('select');
    select.setAttribute('aria-label', 'Shop by category');
    select.setAttribute('data-component-family-select', '');
    links.forEach((link, index) => {
      const option = document.createElement('option');
      option.value = String(index);
      option.textContent = link.textContent.trim();
      select.append(option);
    });
    const custom = document.createElement('option');
    custom.value = 'custom';
    custom.textContent = 'Custom selection';
    custom.disabled = true;
    custom.hidden = true;
    select.append(custom);
    select.addEventListener('change', () => links[Number(select.value)]?.click());
    label.append(heading, select);
    navigation.before(label);
    navigation.dataset.categorySelectReady = 'true';
    return select;
  };

  const initQuickView = (center) => {
    const shell = center.querySelector('[data-component-quick-view-dialog]');
    const body = shell?.querySelector('[data-component-quick-view-body]');
    const drawer = shell?.querySelector('.component-quick-view__drawer');
    if (!shell || !body || !drawer) return;
    let opener = null;
    let requestVersion = 0;
    const close = () => { requestVersion += 1; shell.hidden = true; document.documentElement.classList.remove('component-quick-view-open'); body.replaceChildren(); opener?.focus?.(); };
    shell.addEventListener('click', (event) => {
      if (event.target.closest('[data-quick-view-close]') || !drawer.contains(event.target)) close();
    });
    shell.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); close(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),select,input')].filter((element) => !element.hidden);
      if (!focusable.length) return;
      if (event.shiftKey && document.activeElement === focusable[0]) { event.preventDefault(); focusable.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === focusable.at(-1)) { event.preventDefault(); focusable[0].focus(); }
    });
    const bindForm = () => {
      const form = body.querySelector('[data-component-quick-form]');
      if (!form) return;
      const variants = JSON.parse(form.querySelector('[data-quick-variants]')?.textContent || '[]');
      const options = [...form.querySelectorAll('[data-quick-option]')];
      const id = form.querySelector('[data-quick-variant-id]');
      const qty = form.querySelector('[data-quick-quantity]');
      const total = form.querySelector('[data-quick-total]');
      const submit = form.querySelector('button[type="submit"]');
      const update = () => {
        const chosen = options.map((option) => option.value);
        const variant = variants.find((entry) => chosen.every((value, index) => entry.options?.[index] === value)) || variants[0];
        if (id && variant) id.value = variant.id;
        const priceBlocked = !variant || Number(variant.price || 0) <= 0;
        if (total && variant) {
          const fallbackPriceText = priceBlocked ? 'Contact AVID for pricing' : '';
          if (priceBlocked) total.textContent = fallbackPriceText;
          else {
            const priceText = money(Number(variant.price) * Math.max(1, Number(qty?.value || 1)), center.dataset.currency || 'USD');
            const price = globalThis.AvidDealerPricing?.createPriceElement?.({
              variantIds: variant.id,
              priceText,
              priceCents: Number(variant.price) * Math.max(1, Number(qty?.value || 1)),
              compareAtPriceCents: Number(variant.compare_at_price || 0) * Math.max(1, Number(qty?.value || 1)),
              compareAtPriceText: money(Number(variant.compare_at_price || 0) * Math.max(1, Number(qty?.value || 1)), center.dataset.currency || 'USD'),
              priceMultiplier: Math.max(1, Number(qty?.value || 1))
            });
            if (price) {
              total.replaceChildren(price);
              void globalThis.AvidDealerPricing.hydrate(price);
            } else total.textContent = priceText;
          }
        }
        if (submit) { const blocked = priceBlocked || !variant.available; submit.disabled = blocked; submit.textContent = priceBlocked ? 'Price unavailable' : (!variant.available ? 'Unavailable' : 'Add to Cart'); }
      };
      options.forEach((option) => option.addEventListener('change', update)); qty?.addEventListener('input', update); update();
      form.addEventListener('submit', () => window.setTimeout(close, 450));
    };
    const open = async (url, trigger) => {
      const version = ++requestVersion;
      opener = trigger; shell.hidden = false; document.documentElement.classList.add('component-quick-view-open'); body.innerHTML = '<p class="component-quick-view__loading" role="status">Loading product details…</p>'; drawer.querySelector('[data-quick-view-close]')?.focus();
      try { const target = sectionUrl(url, 'component-quick-view'); const response = await fetch(target, { credentials: 'same-origin', headers: { Accept: 'text/html' } }); if (!response.ok) throw new Error(`Quick View returned ${response.status}`); const html = await response.text(); if (version !== requestVersion) return; body.innerHTML = html; body.querySelectorAll('a[href]').forEach((link) => { link.href = previewUrl(link.getAttribute('href')).toString(); }); bindForm(); body.querySelector('select,input,button,a')?.focus(); }
      catch (error) { if (version !== requestVersion) return; console.warn('AVID Quick View could not load.', error); body.innerHTML = `<div class="component-quick-view__error"><strong>Product details could not be loaded.</strong><a href="${previewUrl(url)}">Open Full Product Details</a></div>`; }
    };
    center.addEventListener('click', (event) => { const trigger = event.target.closest('[data-component-quick-view]'); if (trigger) { event.preventDefault(); open(trigger.dataset.componentQuickView, trigger); } });
  };


  const matchesCollectionSearch = (text, terms, fixedTerms = []) => {
    const source = normalize(text);
    const matchesTerm = (term) => {
      const needle = normalize(term);
      if (!needle) return true;
      let index = source.indexOf(needle);
      while (index !== -1) {
        if (index === 0 || !/[\p{L}\p{N}]/u.test(source[index - 1])) return true;
        index = source.indexOf(needle, index + 1);
      }
      return false;
    };
    return (!fixedTerms.length || fixedTerms.some((term) => source.includes(term))) && terms.every(matchesTerm);
  };
  const retryCollectionRead = async (read, wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try { return await read(); }
      catch (error) {
        if (error.name === 'AbortError' || attempt === 2) throw error;
        await wait(500 * (attempt + 1));
      }
    }
  };
  const collectCollectionPages = async (pages, currentPage, readPage) => {
    const pending = Array.from({ length: pages }, (_, index) => index + 1).filter((page) => page !== currentPage);
    const results = [];
    await Promise.all(Array.from({ length: Math.min(3, pending.length) }, async () => {
      while (pending.length) { const page = pending.shift(); results.push([page, await readPage(page)]); }
    }));
    return results;
  };

  const initExplorer = (center, explorer) => {
    if (explorer.dataset.componentExplorerReady === 'true') return;
    explorer.dataset.componentExplorerReady = 'true';
    const host = explorer.querySelector('[data-component-results-host]'); const status = explorer.querySelector('[data-component-status]'); const baseUrl = explorer.dataset.collectionUrl; const fixedTerms = normalize(explorer.dataset.fixedTerms).split('|').map((term) => term.trim()).filter(Boolean); let controller = null;
    let serverResultsPending = Boolean(host.querySelector('[data-component-results-fragment]'));
    const bindFilterControls = (scope, fragment) => {
      scope.querySelectorAll('[data-center-filter-link]').forEach((link) => link.addEventListener('click', (event) => { event.preventDefault(); load(link.href, true); }));
      scope.querySelector('[data-center-sort]')?.addEventListener('change', (event) => { const url = new URL(fragment.dataset.loadedUrl || fragment.dataset.collectionUrl || baseUrl, window.location.origin); url.searchParams.set('sort_by', event.target.value); url.searchParams.delete('page'); load(url, true); });
      scope.querySelector('[data-center-price-form]')?.addEventListener('submit', (event) => { event.preventDefault(); const url = new URL(fragment.dataset.loadedUrl || fragment.dataset.collectionUrl || baseUrl, window.location.origin); new FormData(event.currentTarget).forEach((value, key) => { if (String(value).trim()) url.searchParams.set(key, value); else url.searchParams.delete(key); }); url.searchParams.delete('page'); load(url, true); });
    };
    const loadServerFilters = async (fragment, url, signal) => {
      const pending = fragment.querySelector('[data-component-filters-pending]');
      if (!pending) return;
      try {
        const response = await retryCollectionRead(async () => {
          const response = await fetch(sectionUrl(url), { credentials: 'same-origin', signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Collection filters could not load.');
          return response;
        });
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (signal.aborted || !fragment.isConnected) return;
        const filters = parsed.querySelector('.component-results__filters');
        const current = pending.closest('.component-results__filters');
        if (!filters || !current) throw new Error('Collection filters are unavailable.');
        current.replaceChildren(...filters.childNodes);
        bindFilterControls(current, fragment);
        globalThis.AvidStorefrontUI?.init(current);
      } catch (error) {
        if (error.name !== 'AbortError' && pending.isConnected) pending.firstChild.textContent = 'Filters could not load. ';
      }
    };
    const bindFragment = () => {
      const fragment = host.querySelector('[data-component-results-fragment]'); if (!fragment) return;
      bindFilterControls(fragment, fragment);
      globalThis.AvidStorefrontUI?.init(fragment);
      const search = fragment.querySelector('[data-center-local-search]');
      const count = fragment.querySelector('[data-center-count]');
      const grid = fragment.querySelector('[data-center-product-grid]');
      const pagination = fragment.querySelector('.component-results__pagination');
      const sourceCount = count?.textContent || '0';
      const originalCards = [...fragment.querySelectorAll('[data-center-product]')];
      let cards = originalCards;
      let complete = Number(grid?.dataset.totalPages || 1) <= 1;
      let indexRequest = null;
      let timer;
      const vehicleBrowsing=Boolean(center.querySelector('[data-component-vehicle-selector]'));
      const cardProjection=card=>{try{return JSON.parse(card.querySelector('[data-catalog-fitment]')?.textContent||'null');}catch(_){return null;}};
      const specificationFilters = fragment.querySelector('[data-bearing-filters], [data-specification-filters]');
      const specificationParameter = specificationFilters?.dataset.specificationFilters || 'bearing_specs';
      let specificationSelection = {};
      if (specificationFilters) {
        try { specificationSelection = JSON.parse(new URL(window.location.href).searchParams.get(specificationParameter) || '{}'); } catch (_error) { /* Ignore malformed bookmarks. */ }
        if (!specificationSelection || Array.isArray(specificationSelection) || typeof specificationSelection !== 'object') specificationSelection = {};
        specificationSelection = Object.fromEntries(Object.entries(specificationSelection).filter(([, values]) => Array.isArray(values) && values.every(value => typeof value === 'string')));
      }
      const cardSpecifications = (card) => {
        const values = new Map(['brake_line_specs', 'bung_specs', 'cup_specs', 'washer_specs', 'builder_specs', 'slug_specs', 'titanium_fab_specs'].includes(specificationParameter) ? [] : [['Product type', card.dataset.productType]]);
        card.querySelectorAll('[data-brake-line-specifications] .spec-row, [data-bearing-specifications] .spec-row, [data-misalignment-specifications] .spec-row, [data-rod-specifications] .spec-row, [data-cup-specifications] .spec-row, [data-washer-specifications] .spec-row, [data-builder-specifications] .spec-row, [data-slug-specifications] .spec-row, [data-avid-spec-family="bung"] .spec-row').forEach(row => {
          values.set(row.querySelector('dt').textContent.trim(), row.querySelector('dd').textContent.trim());
        });
        return values;
      };
      const matchesSpecifications = (card, except) => {
        const values = cardSpecifications(card);
        return Object.entries(specificationSelection).every(([key, selected]) => key === except || !selected.length || selected.includes(values.get(key)));
      };
      const renderSpecificationFilters = () => {
        if (!specificationFilters || !complete) return;
        const opened = new Set([...specificationFilters.querySelectorAll('details[open]')].map(el => el.dataset.specification));
        const facets = new Map();
        cards.forEach(card => cardSpecifications(card).forEach((value, key) => {
          if (!value) return;
          if (!facets.has(key)) facets.set(key, new Set());
          facets.get(key).add(value);
        }));
        Object.entries(specificationSelection).forEach(([key, values]) => {
          if (!facets.has(key)) facets.set(key, new Set());
          values.forEach(value => facets.get(key).add(value));
        });
        const nodes = [];
        const clear = document.createElement('button');
        clear.type = 'button'; clear.className = 'button button--secondary';
        clear.textContent = 'Clear specification filters';
        clear.hidden = !Object.values(specificationSelection).some(values => values.length);
        clear.addEventListener('click', () => { specificationSelection = {}; void applySearch(); });
        nodes.push(clear);
        facets.forEach((values, key) => {
          const details = document.createElement('details'); details.dataset.specification = key;
          details.open = opened.has(key) || Boolean(specificationSelection[key]?.length);
          const summary = document.createElement('summary'); summary.textContent = key;
          const list = document.createElement('ul');
          [...values].sort((a,b) => a.localeCompare(b, undefined, {numeric:true})).forEach(value => {
            const li = document.createElement('li'); const label = document.createElement('label');
            label.className = 'component-filter-choice';
            const input = document.createElement('input'); input.type = 'checkbox'; input.checked = Boolean(specificationSelection[key]?.includes(value));
            const text = document.createElement('span'); text.textContent = value;
            const count = document.createElement('small');
            count.textContent = String(cards.filter(card => cardSpecifications(card).get(key) === value && matchesSpecifications(card, key)).length);
            input.addEventListener('change', () => {
              const selected = new Set(specificationSelection[key] || []);
              if (input.checked) selected.add(value); else selected.delete(value);
              specificationSelection[key] = [...selected]; void applySearch();
              const replacement = [...specificationFilters.querySelectorAll('details')].find(el => el.dataset.specification === key);
              [...(replacement?.querySelectorAll('label') || [])].find(el => el.querySelector('span').textContent === value)?.querySelector('input')?.focus();
            });
            label.append(input, text, count); li.append(label); list.append(li);
          });
          details.append(summary, list); nodes.push(details);
        });
        specificationFilters.replaceChildren(...nodes);
      };
      const message = document.createElement('p');
      message.className = 'component-results__search-status';
      message.setAttribute('role', 'status');
      search?.closest('label')?.after(message);
      search.value = new URL(window.location.href).searchParams.get('component_search') || '';
      const buildIndex = async () => {
        if (complete) return;
        if (indexRequest) return indexRequest;
        indexRequest = (async () => {
          const currentPage = Number(grid.dataset.currentPage || 1);
          const pages = Number(grid.dataset.totalPages || 1);
          const url = sectionUrl(fragment.dataset.loadedUrl || fragment.dataset.collectionUrl || baseUrl);
          const batches = await collectCollectionPages(pages, currentPage, (page) => retryCollectionRead(async () => {
            const target = new URL(url); target.searchParams.set('page', String(page));
            const response = await fetch(target, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Collection search could not be completed.');
            const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
            const pageGrid=parsed.querySelector('[data-center-product-grid]');
            if (!pageGrid||Number(pageGrid.dataset.currentPage)!==page||Number(pageGrid.dataset.totalPages)!==pages) throw new Error('Collection search response is incomplete or changed during loading.');
            return [...parsed.querySelectorAll('[data-center-product]')];
          }));
          if (!fragment.isConnected) return;
          const byPage = new Map(batches); byPage.set(currentPage, originalCards);
          cards = [...byPage].sort(([a], [b]) => a - b).flatMap(([, items]) => items);
          if(vehicleBrowsing&&(cards.some(card=>!card.dataset.productId)||new Set(cards.map(card=>card.dataset.productId)).size!==cards.length))throw new Error('Vehicle collection contains missing or duplicate product identities.');
          grid.replaceChildren(...cards); complete = true;
        })().catch((error) => { indexRequest = null; throw error; });
        return indexRequest;
      };
      const applySearch = async () => {
        const terms = normalize(search?.value).split(/\s+/).filter(Boolean);
        if (controller?.signal.aborted || !fragment.isConnected) return;
        if (specificationParameter === 'cup_specs' && complete) {
          // Old bookmarks must not resurrect fields removed from the cup browser.
          const available = new Set(cards.flatMap(card => [...cardSpecifications(card).keys()]));
          specificationSelection = Object.fromEntries(Object.entries(specificationSelection).filter(([key]) => available.has(key)));
          if (specificationSelection.Retention) specificationSelection.Retention = [...new Set(specificationSelection.Retention.map(value => ['Snap ring', 'Retaining ring'].includes(value) ? 'Snap ring / retaining ring' : value))];
        }
        const vehicleContext=vehicleBrowsing?globalThis.AvidVehicleSelector?.browseContext():null;
        const filtering = Boolean(vehicleContext) || terms.length > 0 || fixedTerms.length > 0 || Object.values(specificationSelection).some(values => values.length);
        const page = new URL(window.location.href);
        if (search.value.trim()) page.searchParams.set('component_search', search.value.trim()); else page.searchParams.delete('component_search');
        if (specificationFilters) {
          if (Object.values(specificationSelection).some(values => values.length)) page.searchParams.set(specificationParameter, JSON.stringify(specificationSelection));
          else page.searchParams.delete(specificationParameter);
        }
        window.history.replaceState({}, '', `${page.pathname}${page.search}`);
        if ((filtering || specificationFilters) && !complete) {
          message.textContent = 'Searching the complete collection...';
          grid.setAttribute('aria-busy', 'true');
          try { await buildIndex(); }
          catch (error) {
            if (error.name !== 'AbortError' && fragment.isConnected) message.textContent = 'Search could not reach the complete collection. Change the search to retry.';
            return;
          } finally { grid.removeAttribute('aria-busy'); }
          if (!fragment.isConnected) return;
          // The customer may have changed or cleared the query while loading.
          return applySearch();
        }
        let visible = 0;
        cards.forEach((card) => {
          const matches = filtering ? matchesCollectionSearch(card.dataset.searchText + ' ' + (card.querySelector('[data-card-specifications]')?.textContent || ''), terms, fixedTerms) && matchesSpecifications(card) && (!vehicleContext||globalThis.AvidVehicleSelector.matchesDiscovery(cardProjection(card),vehicleContext)) : originalCards.includes(card);
          card.hidden = !matches; if (matches) visible += 1;
          card.querySelectorAll('a[href]').forEach(link=>{
            const original=link.dataset.fitmentOriginalHref || link.getAttribute('href');
            link.dataset.fitmentOriginalHref=original;
            const target=globalThis.AvidVehicleSelector?.withNavigationContext(original);
            if(!target || target.origin!==window.location.origin || !target.pathname.startsWith('/products/'))return;
            link.href=vehicleContext?.applicationId
              ? globalThis.AvidVehicleSelector.destination({url:target.toString()},vehicleContext)
              : target.toString();
          });
        });
        if (pagination) pagination.hidden = filtering;
        const totalCount = specificationFilters && complete ? String(cards.length) : sourceCount;
        if (count) count.textContent = filtering ? String(visible) : totalCount;
        const summary = center.querySelector('[data-center-summary-count]');
        if (summary) summary.textContent = filtering ? String(visible) : totalCount;
        message.textContent = filtering ? (visible ? `${visible} matching products in this complete collection.` : 'No products match. Clear the search or change filters.') : '';
        void globalThis.AvidDealerPricing?.hydrate(grid);
        renderSpecificationFilters();
      };
      search?.addEventListener('input', () => { window.clearTimeout(timer); timer = window.setTimeout(applySearch, 300); });
      if(vehicleBrowsing)document.addEventListener('avid:vehicle-context-change',()=>{void applySearch();},{signal:controller.signal});
      void applySearch();
    };
    const load = async (url, history = false) => {
      const filtersWereOpen = host.querySelector('[data-sf-filter-disclosure]')?.open;
      // A filter or sort click can arrive before the search input debounce runs.
      // Persist the current input before the replacement fragment restores it.
      if (history) {
        const search = host.querySelector('[data-center-local-search]');
        if (search) {
          const page = new URL(window.location.href);
          if (search.value.trim()) page.searchParams.set('component_search', search.value.trim()); else page.searchParams.delete('component_search');
          window.history.replaceState({}, '', `${page.pathname}${page.search}`);
        }
      }
      const requestedUrl = url || requestedResultsUrl(window.location.href, explorer.dataset.collectionUrl || baseUrl);
      if (serverResultsPending) {
        serverResultsPending = false;
        const serverUrl = new URL(explorer.dataset.initialResultsUrl || window.location.href, window.location.origin);
        const serverPage = Number(host.querySelector('[data-center-product-grid]')?.dataset.currentPage || 1);
        if (explorer.dataset.initialResultsUrl && serverPage > 1) serverUrl.searchParams.set('page', String(serverPage));
        if (canReuseServerResults(serverUrl, requestedUrl)) {
          controller = new AbortController();
          host.querySelector('[data-component-results-fragment]').dataset.loadedUrl = new URL(requestedUrl, window.location.origin).toString();
          if (status) status.hidden = true;
          bindFragment();
          void loadServerFilters(host.querySelector('[data-component-results-fragment]'), requestedUrl, controller.signal);
          void globalThis.AvidDealerPricing?.hydrate(host);
          center.dispatchEvent(new CustomEvent('componentcenter:results', { detail: { url: new URL(requestedUrl, window.location.origin).toString() } }));
          return;
        }
      }
      controller?.abort(); controller = new AbortController(); explorer.setAttribute('aria-busy', 'true'); if (status) { status.hidden = false; status.textContent = 'Loading reviewed Shopify products and filters…'; }
      try { const target = sectionUrl(requestedUrl); const response = await fetch(target, { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' } }); if (!response.ok) throw new Error(`Collection filters returned ${response.status}`); host.innerHTML = await response.text(); const loaded = host.querySelector('[data-component-results-fragment]'); if (loaded) loaded.dataset.loadedUrl = new URL(requestedUrl, window.location.origin).toString(); if (status) status.hidden = true; bindFragment(); const filters = host.querySelector('[data-sf-filter-disclosure]'); if (filters && filtersWereOpen !== undefined) filters.open = filtersWereOpen; void globalThis.AvidDealerPricing?.hydrate(host); center.dispatchEvent(new CustomEvent('componentcenter:results', { detail: { url: new URL(requestedUrl, window.location.origin).toString() } })); if (history) { const publicUrl = new URL(requestedUrl, window.location.origin); publicUrl.searchParams.delete('section_id'); const page = new URL(window.location.href); page.searchParams.set('component_filters', `${publicUrl.pathname}${publicUrl.search}`); window.history.pushState({}, '', `${page.pathname}${page.search}`); } }
      catch (error) { if (error.name === 'AbortError') return; console.warn('AVID Component Center could not load collection filters.', error); if (status) status.textContent = 'This category could not be loaded. Open the full collection or retry.'; }
      finally { explorer.setAttribute('aria-busy', 'false'); }
    };
    explorer.avidLoad = load;
    explorer.avidCancel = () => controller?.abort();
  };

  const initCenter = (center) => {
    if (center.dataset.componentCenterReady === 'true') return;
    center.dataset.componentCenterReady = 'true';
    const vehicleForm=center.querySelector('[data-component-vehicle-selector]');
    if(vehicleForm){
      globalThis.AvidVehicleSelector?.bind(vehicleForm,undefined,undefined,()=>{});
      vehicleForm.querySelector('[data-component-vehicle-clear]')?.addEventListener('click',()=>globalThis.AvidVehicleSelector?.clear());
    }
    const tabs = [...center.querySelectorAll('[data-component-tab]')]; const panels = [...center.querySelectorAll('[data-component-panel]')];
    const familyLinks = [...center.querySelectorAll('[data-component-family-link]')];
    const familyExplorer = familyLinks.length ? center.querySelector('[data-component-panel="all"] [data-component-explorer]') : null;
    const familyCollections = familyLinks.map((link) => link.dataset.componentFamilyCollection).filter(Boolean);
    let familySelect = null;
    const syncFamilyLinks = (url) => {
      familyLinks.forEach((link) => {
        const selected = familySelected(link, url, familyExplorer.dataset.collectionUrl);
        if (selected) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
      });
      if (familySelect) {
        const selectedIndex = familyLinks.findIndex((link) => link.getAttribute('aria-current') === 'page');
        familySelect.value = selectedIndex < 0 ? 'custom' : String(selectedIndex);
      }
    };
    center.querySelectorAll('[data-component-explorer]').forEach((explorer) => initExplorer(center, explorer)); initQuickView(center);
    if (familyExplorer?.avidLoad) familySelect = createFamilySelect(center.querySelector('[data-vehicle-family-navigation]'), familyLinks);
    const activate = (id, { focus = false, updateUrl = false, resultsUrl = null } = {}) => {
      const requested = normalize(id);
      const selectedId = panels.some((panel) => normalize(panel.dataset.componentPanel) === requested) ? requested : normalize(center.dataset.initialCategory || panels[0]?.dataset.componentPanel);
      if (updateUrl) {
        const url = new URL(window.location.href);
        for (const key of [...url.searchParams.keys()]) {
          if (key.startsWith('filter.') || ['ti_kit', 'ti_specialty', 'component_filters', 'component_search', 'sort_by', 'page'].includes(key)) url.searchParams.delete(key);
        }
        if (selectedId === 'all') url.searchParams.delete('component_category'); else url.searchParams.set('component_category', selectedId);
        window.history.pushState({}, '', `${url.pathname}${url.search}`);
      }
      tabs.forEach((tab) => { const selected = normalize(tab.dataset.componentTab) === selectedId; tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1; if (selected && focus) tab.focus(); });
      panels.forEach((panel) => {
        const selected = normalize(panel.dataset.componentPanel) === selectedId;
        panel.hidden = !selected; panel.setAttribute('aria-hidden', String(!selected));
        const explorer = panel.querySelector('[data-component-explorer]');
        if (selected) explorer?.avidLoad?.(resultsUrl); else explorer?.avidCancel?.();
      });
    };
    tabs.forEach((tab) => { tab.addEventListener('click', () => activate(tab.dataset.componentTab, { updateUrl: true })); tab.addEventListener('keydown', (event) => { const visible = tabs.filter((item) => !item.hidden); const current = visible.indexOf(tab); let target = current; if (event.key === 'ArrowRight') target = (current + 1) % visible.length; else if (event.key === 'ArrowLeft') target = (current - 1 + visible.length) % visible.length; else if (event.key === 'Home') target = 0; else if (event.key === 'End') target = visible.length - 1; else return; event.preventDefault(); activate(visible[target].dataset.componentTab, { focus: true, updateUrl: true }); }); });
    familyLinks.forEach((link) => link.addEventListener('click', (event) => { if (!familyExplorer?.avidLoad || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; event.preventDefault(); const url = link.dataset.componentFamilyCollection || link.href; syncFamilyLinks(url); familyExplorer.avidLoad(url, true); }));
    center.addEventListener('componentcenter:results', (event) => syncFamilyLinks(event.detail.url));
    window.addEventListener('popstate', () => {
      if (familyExplorer) {
        const resultsUrl = requestedResultsUrl(window.location.href, familyExplorer.dataset.collectionUrl, familyCollections);
        syncFamilyLinks(resultsUrl);
        activate('all', { resultsUrl });
      } else activate(requestedCategory(window.location.href, center.dataset.initialCategory));
    });
    const initialResultsUrl = familyExplorer ? requestedResultsUrl(window.location.href, familyExplorer.dataset.collectionUrl, familyCollections) : null;
    if (initialResultsUrl) syncFamilyLinks(initialResultsUrl);
    activate(requestedCategory(window.location.href, center.dataset.initialCategory), { resultsUrl: initialResultsUrl });
  };
  if (testHook) Object.assign(testHook, { createFamilySelect, canReuseServerResults, retryCollectionRead, collectCollectionPages, matchesCollectionSearch, normalize, previewUrl, sectionUrl, requestedCategory, requestedResultsUrl, requestedFamilies, familyValues, sameFamilies, familySelected });
  const init = (scope = document) => scope.querySelectorAll('[data-component-center]').forEach(initCenter);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
